/**
 * Imgly Background Removal — client-side entity executor.
 *
 * Wraps `@imgly/background-removal` — a WASM/ONNX model that runs entirely
 * in the user's browser. Registered at module load against the imgly
 * background-removal entity (see
 * `packages/entities/src/integrations/imgly-background-removal/config.ts`).
 *
 * The 25 MiB input cap is enforced here rather than in the bridge handler
 * or the registry — per-entity input constraints are the executor's
 * responsibility, mirroring how server-side entities own their input
 * validation. The bridge applies a defense-in-depth SDK-side precheck too.
 */

import { registerClientEntity } from '../client-entity-registry';

/**
 * Entity ID of the imgly background-removal client-side entity. Literal
 * here so the client never imports `@fias/entities` (server-only
 * transitive deps in some integrations would otherwise bloat the bundle
 * or fail at startup).
 *
 * SYNC NOTE: must match `IMGLY_BACKGROUND_REMOVAL_ENTITY_ID` in
 * `packages/entities/src/integrations/imgly-background-removal/config.ts`.
 * The architecture test
 * `client-entity-registry-mirrors-entity-config.test.ts` enforces this.
 */
export const IMGLY_BACKGROUND_REMOVAL_ENTITY_ID = 'ent_intg_imgly_bgrm_000000000000';

export interface ImglyBackgroundRemovalInput {
  /** Source image. PNG or JPEG. Maximum 25 MiB (26,214,400 bytes). */
  image: Blob;
}

export interface ImglyBackgroundRemovalResult {
  /** Cutout image as a transparent-background PNG. */
  image: Blob;
}

/** Maximum input size — matches the SDK-side precheck and the bridge guard. */
export const IMGLY_MAX_INPUT_BYTES = 25 * 1024 * 1024;

type ImglyModule = typeof import('@imgly/background-removal');
type ImglyConfig = Parameters<ImglyModule['removeBackground']>[1];

/**
 * Run inference off the main thread so the UI stays responsive (the
 * editor's processing overlay + spinner must keep animating while this
 * runs). In CPU mode imgly executes the ONNX model synchronously on the
 * main thread, which blocks it for the multi-second inference and freezes
 * every animation — exactly when the user most needs the "still working"
 * cue. `device: 'gpu'` moves the compute onto WebGPU, and `proxyToWorker`
 * moves the ORT runtime into a Web Worker.
 *
 * Both degrade safely: imgly's `webgpu()` capability check turns `device`
 * back to CPU when WebGPU is unavailable, and `proxyToWorker` is honored
 * only alongside WebGPU (`proxyToWorker = useWebGPU && config.proxyToWorker`
 * in imgly's source). So on a WebGPU-capable browser the main thread stays
 * free; on one without, behavior is identical to before — never worse.
 */
const REMOVE_BG_CONFIG: ImglyConfig = {
  device: 'gpu',
  proxyToWorker: true,
};

let modulePromise: Promise<ImglyModule> | null = null;

function loadImgly(): Promise<ImglyModule> {
  if (!modulePromise) {
    // If the dynamic import fails (chunk-load error, transient CDN hiccup),
    // clear the cached rejected promise so the next call retries from
    // scratch instead of forever re-throwing the same error.
    modulePromise = import('@imgly/background-removal').catch((err) => {
      modulePromise = null;
      throw err;
    });
  }
  return modulePromise;
}

// imgly's `removeBackground` is undocumented for concurrent invocation and
// uses a shared ONNX runtime session under the hood. The registry can be
// reached from internal arches AND from the plugin bridge concurrently —
// serialize all calls on a tail promise. The `.catch(() => {})` ensures a
// failure in one call does NOT poison the chain for subsequent calls.
let tail: Promise<void> = Promise.resolve();

export async function imglyBackgroundRemovalExecutor(
  input: ImglyBackgroundRemovalInput,
): Promise<ImglyBackgroundRemovalResult> {
  if (!input || !(input.image instanceof Blob)) {
    throw new Error('imgly-background-removal: input.image must be a Blob');
  }
  if (input.image.size > IMGLY_MAX_INPUT_BYTES) {
    throw new Error(
      `imgly-background-removal: image exceeds 25 MiB limit (got ${input.image.size} bytes)`,
    );
  }

  const result = tail.then(async () => {
    const imgly = await loadImgly();
    return imgly.removeBackground(input.image, REMOVE_BG_CONFIG);
  });
  tail = result.then(
    () => {},
    () => {},
  );
  const image = await result;
  return { image };
}

/**
 * Warm the WASM bundle so the first user-triggered call avoids the
 * download latency. No-op if already loaded. Optional — call from a
 * component mount when you expect the feature to be used soon.
 */
export async function prefetchImglyBackgroundRemoval(): Promise<void> {
  await loadImgly();
}

registerClientEntity(IMGLY_BACKGROUND_REMOVAL_ENTITY_ID, {
  executor: imglyBackgroundRemovalExecutor,
  rateLimitPerMinute: 10,
  pluginPermission: 'entities:image_remove_background',
});
